/**
 * Визуальное затемнение элементов вне modeling focus выбранного viewpoint:
 * SVG filter preset `saturate` через {@link https://x6.antv.antgroup.com/en/api/graph/defs Graph#defineFilter}
 * + лёгкая прозрачность. В @antv/x6 3.x нет `graph.filter` — используется `graph.defineFilter`.
 */
import {
	isElementTypeInViewpointFocus,
	isEdgeEndpointsInViewpointFocus,
} from './archimate31ViewpointLayerFocus';

/** Доля обесцвечивания для preset `saturate` в X6: чем больше, тем сильнее сдвиг к серому. */
const DIM_SATURATE_AMOUNT = 0.62;
/** Прозрачность затемнённых примитивов (1 = непрозрачно). */
const DIM_OPACITY = 0.72;

/**
 * Снять filter/opacity, выставленные этим модулем (по всем селекторам верхнего уровня attrs).
 * @param {import('@antv/x6').Cell} cell
 */
function clearViewpointVisualAttrs(cell) {
	if (!cell || typeof cell.getAttrs !== 'function') return;
	const attrs = cell.getAttrs();
	if (!attrs || typeof attrs !== 'object') return;
	for (const sel of Object.keys(attrs)) {
		if (attrs[sel] == null || typeof attrs[sel] !== 'object') continue;
		const base = `${sel}`;
		if (typeof cell.removeAttrByPath === 'function') {
			cell.removeAttrByPath(`${base}/filter`);
			cell.removeAttrByPath(`${base}/opacity`);
		} else {
			cell.attr(`${base}/filter`, null);
			cell.attr(`${base}/opacity`, null);
		}
	}
}

/**
 * @param {import('@antv/x6').Cell} cell
 * @param {string} [filterUrl] — `url(#id)` из graph.defineFilter(); пусто — только opacity
 */
function applyDimToCellSelectors(cell, filterUrl) {
	if (!cell || typeof cell.getAttrs !== 'function') return;
	const attrs = cell.getAttrs();
	if (!attrs || typeof attrs !== 'object') return;
	for (const sel of Object.keys(attrs)) {
		if (attrs[sel] == null || typeof attrs[sel] !== 'object') continue;
		if (filterUrl) {
			cell.attr(`${sel}/filter`, filterUrl);
		} else if (typeof cell.removeAttrByPath === 'function') {
			cell.removeAttrByPath(`${sel}/filter`);
		}
		cell.attr(`${sel}/opacity`, DIM_OPACITY);
	}
}

/**
 * Обновить затемнение по viewpoint текущего view.
 * @param {import('../ModelerX6').default} modeler
 */
export function applyViewpointDimming(modeler) {
	const graph = modeler?.getGraph?.();
	if (!graph || typeof graph.getCells !== 'function') return;

	const cells = graph.getCells();
	for (const cell of cells) {
		clearViewpointVisualAttrs(cell);
	}

	const viewpoint = modeler.getActiveViewViewpoint?.();
	if (viewpoint == null || String(viewpoint).trim() === '') return;

	/** @type {string} */
	let filterUrl = '';
	try {
		const defineFilter =
			typeof graph.defineFilter === 'function'
				? graph.defineFilter.bind(graph)
				: graph.defs && typeof graph.defs.filter === 'function'
					? graph.defs.filter.bind(graph.defs)
					: null;
		if (defineFilter) {
			const id = defineFilter({
				name: 'saturate',
				args: {amount: DIM_SATURATE_AMOUNT},
			});
			if (id) filterUrl = `url(#${id})`;
		}
	} catch (_) {
		/* без фильтра остаётся только opacity */
	}

	/** @type {Map<string, boolean>} */
	const nodeFocusById = new Map();
	for (const cell of cells) {
		if (!cell || typeof cell.isNode !== 'function' || !cell.isNode()) continue;
		const id = cell.id;
		const data = cell.getData?.() && typeof cell.getData === 'function' ? cell.getData() : {};
		const elementType = data?.elementType ?? data?.typeName ?? null;
		const inFocus = isElementTypeInViewpointFocus(viewpoint, elementType);
		nodeFocusById.set(id, inFocus);
	}

	for (const cell of cells) {
		if (!cell || typeof cell.isNode !== 'function' || !cell.isNode()) continue;
		const id = cell.id;
		const data = cell.getData?.() && typeof cell.getData === 'function' ? cell.getData() : {};
		const elementType = data?.elementType ?? data?.typeName ?? null;
		if (isElementTypeInViewpointFocus(viewpoint, elementType)) continue;
		applyDimToCellSelectors(cell, filterUrl);
	}

	for (const cell of cells) {
		if (!cell || typeof cell.isEdge !== 'function' || !cell.isEdge()) continue;
		const srcId =
			typeof cell.getSourceCellId === 'function'
				? cell.getSourceCellId()
				: cell.getSource?.()?.cell;
		const tgtId =
			typeof cell.getTargetCellId === 'function'
				? cell.getTargetCellId()
				: cell.getTarget?.()?.cell;
		const srcFocus = srcId != null ? (nodeFocusById.get(srcId) ?? true) : true;
		const tgtFocus = tgtId != null ? (nodeFocusById.get(tgtId) ?? true) : true;
		if (isEdgeEndpointsInViewpointFocus(viewpoint, srcFocus, tgtFocus)) continue;
		applyDimToCellSelectors(cell, filterUrl);
	}
}
